import { useId } from 'react';
import { cn } from '../cn';

/** One option in a {@link ChoiceList}. */
export interface ChoiceOption<K extends string> {
  readonly key: K;
  readonly label: string;
  /** A second line under the label ("Due upon signing"). */
  readonly detail?: string;
  /** Right-aligned value ("$3,019.00"). */
  readonly value?: string;
  readonly disabled?: boolean;
}

export interface ChoiceListProps<K extends string> {
  /** Names the group for screen readers. */
  readonly label: string;
  readonly options: readonly ChoiceOption<K>[];
  readonly value: K;
  readonly onChange: (key: K) => void;
  /** Extra content under the chosen option (e.g. an amount field for "Other amount"). */
  readonly renderSelected?: (key: K) => React.ReactNode;
  readonly className?: string;
}

/**
 * A single choice among a few options, shown as selectable rows — for when
 * each option carries a detail and a value a segmented control can't fit
 * (what to pay, which plan). Native radios underneath, so keyboard and
 * screen-reader behavior come for free.
 *
 * @param props - The options, the chosen one, and the change handler.
 * @returns The choice list.
 */
export function ChoiceList<K extends string>({
  label,
  options,
  value,
  onChange,
  renderSelected,
  className,
}: ChoiceListProps<K>): React.ReactElement {
  const name = useId();
  return (
    <fieldset className={cn('space-y-2', className)}>
      <legend className="sr-only">{label}</legend>
      {options.map((option) => {
        const selected = option.key === value;
        const extra = selected && renderSelected ? renderSelected(option.key) : null;
        return (
          <div
            key={option.key}
            className={cn(
              'rounded-portal-button border bg-white transition-colors',
              selected ? 'border-brand shadow-[0_0_0_1px_var(--color-brand)]' : 'border-portal-line hover:border-portal-ink',
              option.disabled && 'opacity-60',
            )}
          >
            <label className="flex cursor-pointer items-center gap-3 p-3">
              <input
                type="radio"
                name={name}
                className="size-4 accent-brand"
                checked={selected}
                disabled={option.disabled}
                onChange={() => onChange(option.key)}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-portal-small font-bold text-portal-ink">{option.label}</span>
                {option.detail ? <span className="block text-portal-micro text-portal-muted">{option.detail}</span> : null}
              </span>
              {option.value ? <span className="text-portal-small font-bold whitespace-nowrap text-portal-ink">{option.value}</span> : null}
            </label>
            {extra ? <div className="px-3 pb-3 pl-10">{extra}</div> : null}
          </div>
        );
      })}
    </fieldset>
  );
}

export interface CheckListProps<K extends string> {
  /** Names the group for screen readers. */
  readonly label: string;
  readonly options: readonly ChoiceOption<K>[];
  /** The ticked options. */
  readonly values: ReadonlySet<K>;
  readonly onToggle: (key: K, checked: boolean) => void;
  /** Extra content under a ticked option (e.g. how much to pay on it). */
  readonly renderChecked?: (key: K) => React.ReactNode;
  readonly className?: string;
}

/**
 * Several choices among a few options, shown as the same selectable rows as
 * {@link ChoiceList} but with checkboxes — for picking which items to act on
 * together (which events to pay). Native checkboxes underneath.
 *
 * @param props - The options, the ticked ones, and the toggle handler.
 * @returns The check list.
 */
export function CheckList<K extends string>({ label, options, values, onToggle, renderChecked, className }: CheckListProps<K>): React.ReactElement {
  return (
    <fieldset className={cn('space-y-2', className)}>
      <legend className="sr-only">{label}</legend>
      {options.map((option) => {
        const checked = values.has(option.key);
        const extra = checked && renderChecked ? renderChecked(option.key) : null;
        return (
          <div
            key={option.key}
            className={cn(
              'rounded-portal-button border bg-white transition-colors',
              checked ? 'border-brand shadow-[0_0_0_1px_var(--color-brand)]' : 'border-portal-line hover:border-portal-ink',
              option.disabled && 'opacity-60',
            )}
          >
            <label className={cn('flex items-center gap-3 p-3', option.disabled ? 'cursor-default' : 'cursor-pointer')}>
              <input
                type="checkbox"
                className="size-4 accent-brand"
                checked={checked}
                disabled={option.disabled}
                onChange={(event) => onToggle(option.key, event.target.checked)}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-portal-small font-bold text-portal-ink">{option.label}</span>
                {option.detail ? <span className="block text-portal-micro text-portal-muted">{option.detail}</span> : null}
              </span>
              {option.value ? <span className="text-portal-small font-bold whitespace-nowrap text-portal-ink">{option.value}</span> : null}
            </label>
            {extra ? <div className="px-3 pb-3 pl-10">{extra}</div> : null}
          </div>
        );
      })}
    </fieldset>
  );
}
